import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, ActivatedRouteSnapshot, Event, NavigationEnd, Router, UrlSegment } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter, map, startWith } from 'rxjs';

import {
    ConfirmationModal,
    ModalData,
} from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { DeactivateDialogConfig } from '@connect-services/client/guards/can-dismount.guard';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { RouteNameEnum } from '@connect-services/client/models/routes.model';

@Injectable({
    providedIn: 'root',
})
export class RouterService {
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly dialog = inject(MatDialog);
    private readonly router = inject(Router);
    private readonly translocoService = inject(TranslocoService);

    public confirmBeforeLeavingPage(config?: DeactivateDialogConfig): Promise<boolean> {
        const dialogRef = this.dialog.open<ConfirmationModal, ModalData>(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: config?.confirmLabel ?? this.translocoService.translate('confirm'),
                description: config?.description,
                title: config?.title,
                type: config?.type,
            },
            disableClose: true,
            width: config?.width ?? '30vw',
        });

        return new Promise<boolean>((resolve) => {
            dialogRef.afterClosed().subscribe((result: ModalResultEnum) => {
                resolve(result === ModalResultEnum.CONFIRM);
            });
        });
    }

    /**
     * At the end of the navigation, this function traverses the route to the last segment.
     * The layout of the page changes depending on the last segment returned.
     */
    public getCurrentRoute(): Signal<RouteNameEnum | undefined> {
        return toSignal(
            this.router.events.pipe(
                filter((event: Event) => event instanceof NavigationEnd),
                startWith(this.router),
                map(() => this.activatedRoute),
                map((route: ActivatedRoute) => {
                    while (route.firstChild) {
                        route = route.firstChild;
                    }

                    return route;
                }),
                filter((route: ActivatedRoute) => route.outlet === 'primary'),
                map((route: ActivatedRoute) => {
                    return route.snapshot?.data?.['name'];
                }),
            ),
        );
    }

    public getUrl(): Signal<string[] | undefined> {
        return toSignal(
            this.router.events.pipe(
                filter((event: Event) => event instanceof NavigationEnd),
                startWith(this.router),
                map(() => this.activatedRoute),
                map((route: ActivatedRoute) => {
                    while (route.firstChild) {
                        route = route.firstChild;
                    }

                    return route;
                }),
                filter((route: ActivatedRoute) => route.outlet === 'primary'),
                map((route: ActivatedRoute) => {
                    if (!route?.snapshot) {
                        return;
                    }

                    return this.getUrlSegments(route.snapshot);
                }),
            ),
        );
    }

    public getUrlParams(): URLSearchParams {
        const currentUrl = new URL(window.location.href);

        return new URLSearchParams(currentUrl.search);
    }

    private getUrlSegments(route: ActivatedRouteSnapshot): string[] {
        return route.pathFromRoot.flatMap((route: ActivatedRouteSnapshot) => {
            return route.url.map((segment: UrlSegment) => {
                return segment.toString();
            });
        });
    }
}
